import AuthCode from "react-auth-code-input";
import { useState, useEffect } from "react";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";
import { MdMailOutline, MdShield, MdSmartphone } from "react-icons/md";
import toast from "react-hot-toast";

/**
 * Modern, sleek OTP & TOTP verification form with fallback handling and recovery codes support
 */
const OTPForm = () => {
  const { handleOTP, mfaType } = useUserCredentials();
  const [value, setValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFallback, setIsFallback] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);

  // Set the correct input code length based on the MFA Type
  // TOTP uses standard 6 digits; Email fallback uses 4 alphanumeric characters
  const isTotpActive = mfaType === "totp" && !isFallback && !useRecovery;
  const currentCodeLength = isTotpActive ? 6 : 4;

  const handleChange = async (val: string) => {
    setValue(val);

    // Automatically verify when the code is fully entered
    if (val.length === currentCodeLength) {
      setIsSubmitting(true);
      try {
        await handleOTP(val, isFallback);
      } catch (error) {
        // Clear input on failure to allow retry
        setValue("");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleRecoverySubmit = async () => {
    if (!recoveryCode) return;
    setIsSubmitting(true);
    try {
      await handleOTP(recoveryCode);
    } catch (error) {
      setRecoveryCode("");
    } finally {
      setIsSubmitting(false);
    }
  };

  const triggerFallbackEmailOTP = async () => {
    // If switching to fallback email OTP, we must call handleOTP with a placeholder or request email trigger
    setIsSubmitting(true);
    try {
      // Trigger OTP generation on backend using empty/trigger code with fallback flag
      await handleOTP("TRIGGER", true);
      setIsFallback(true);
      setUseRecovery(false);
      setValue("");
      toast.success("Verification code sent to your email!");
    } catch (err) {
      toast.error("Failed to send fallback email OTP");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-[480px]">
      {/* Main card */}
      <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-lg md:p-10">
        {/* Centered brand logo */}
        <div className="mb-6 flex justify-center">
          <img src="/ctb-logo-crop.svg" alt="Capture The Bug" className="h-16 w-auto" />
        </div>

        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="mb-3 text-2xl font-semibold text-gray-900 md:text-3xl">
            Verify Your Identity
          </h1>

          {useRecovery ? (
            <>
              <p className="text-sm text-gray-600 md:text-base mb-3">
                Enter one of your 10-character backup recovery codes
              </p>
              <div className="inline-flex items-center gap-2 rounded-full bg-red-50 px-4 py-2 text-sm font-medium text-red-600">
                <MdShield className="h-4 w-4" />
                Backup Mode
              </div>
            </>
          ) : isTotpActive ? (
            <>
              <p className="text-sm text-gray-600 md:text-base mb-3">
                Enter the 6-digit code rotating in your Authenticator app
              </p>
              <div className="inline-flex items-center gap-2 rounded-full bg-[#010D2C]/5 px-4 py-2 text-sm font-medium text-[#010D2C]">
                <MdSmartphone className="h-4 w-4" />
                Authenticator Application
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-600 md:text-base mb-3">
                We've sent a 4-digit verification code to your email
              </p>
              <div className="inline-flex items-center gap-2 rounded-full bg-ctb-blue-50 px-4 py-2 text-sm font-medium text-ctb-blue-600">
                <MdMailOutline className="h-4 w-4" />
                Check your inbox
              </div>
            </>
          )}
        </div>

        {/* Recovery Code Input */}
        {useRecovery ? (
          <div className="mb-6 space-y-4">
            <input
              type="text"
              placeholder="Enter recovery code (e.g. 5A9E1F0B)"
              value={recoveryCode}
              onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
              disabled={isSubmitting}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl text-center font-mono text-lg tracking-widest focus:outline-none focus:ring-2 focus:ring-[#010D2C] placeholder:font-sans placeholder:text-sm placeholder:tracking-normal"
            />
            <button
              type="button"
              onClick={handleRecoverySubmit}
              disabled={recoveryCode.length < 5 || isSubmitting}
              className="w-full py-3 bg-[#010D2C] hover:bg-[#010D2C]/90 text-white rounded-xl font-semibold transition"
            >
              Verify Recovery Code
            </button>
          </div>
        ) : (
          /* OTP / TOTP Input */
          <div className="mb-6">
            <label className="mb-4 block text-center text-sm font-medium text-gray-700">
              Enter verification code
            </label>
            <AuthCode
              key={currentCodeLength} // Re-render input to update length state dynamically
              length={currentCodeLength}
              onChange={handleChange}
              disabled={isSubmitting}
              containerClassName="flex w-full justify-center gap-2 md:gap-3"
              inputClassName={`
                w-10 h-14 md:w-12 md:h-16
                text-2xl font-bold text-center
                bg-gray-50 border-2 border-gray-200
                rounded-xl
                transition-all duration-200
                focus:border-ctb-blue-200 focus:bg-white focus:ring-4 focus:ring-ctb-blue-200/20 focus:outline-none
                hover:border-gray-300
                disabled:opacity-50 disabled:cursor-not-allowed
                ${value.length === currentCodeLength ? 'border-ctb-green-200 bg-ctb-green-50' : ''}
              `}
            />
          </div>
        )}

        {/* Action Fallbacks */}
        <div className="mt-6 flex flex-col gap-2 text-center text-xs">
          {mfaType === "totp" && !isFallback && (
            <button
              type="button"
              onClick={triggerFallbackEmailOTP}
              className="text-[#010D2C] hover:underline font-semibold"
            >
              Use Email OTP Fallback instead
            </button>
          )}

          {mfaType === "totp" && (
            <button
              type="button"
              onClick={() => { setUseRecovery(!useRecovery); setIsFallback(false); setValue(""); }}
              className="text-gray-500 hover:text-gray-700 hover:underline"
            >
              {useRecovery ? "Back to Authenticator App code" : "Use Backup Recovery Code"}
            </button>
          )}
        </div>

        {/* Security note */}
        <div className="mt-8 rounded-lg bg-gray-50 p-4">
          <p className="text-center text-xs text-gray-500">
            🔒 Fully encrypted secure verification session.
          </p>
        </div>
      </div>

      {/* Help text */}
      <p className="mt-6 text-center text-sm text-gray-500">
        Having trouble?{' '}
        <a
          href="mailto:support@capturethebug.xyz"
          className="font-medium text-[#010D2C] transition-colors hover:underline"
        >
          Contact support
        </a>
      </p>
    </div>
  );
};

export default OTPForm;
